<template>
  <div class="comment-content">
    <div
      class="comment__audit-compare-content comment__item-status-content"
      :class="{ 'has-assignees': showReviewAssignees }"
    >
      <div class="comment__audit-compare-content__statuses">
        <StatusComponent
          :value="getStatusById(beforeStatus)"
          class="is-opacity"
        />
        <icon
          data="@icon/arrow-to.svg"
          :fill="true"
          width="var(--icon-24)"
          height="var(--icon-24)"
          color="var(--monochrome05to04)"
        />
        <StatusComponent :value="getStatusById(afterStatus)" />
      </div>

      <AvatarGroupTasks
        v-if="showReviewAssignees"
        :avatars="comment.assignees"
        :numberOfVisibleUsers="5"
        :user="true"
        type="avatarGroup"
        class="comment__review-assignees"
      />
    </div>
    <div
      v-if="!editing && statusChangeReason"
      class="comment-status-reason"
    >
      <BaseSubHeading
        size="medium"
        weight="400"
      >
        <ReferencedText :html="statusChangeReason" />
      </BaseSubHeading>
    </div>
    <div
      v-if="editing"
      class="upload-area__in custom-scroll upload-area__edit"
    >
      <div class="upload-area__edit-row">
        <EditorInput
          id="status-comment"
          :value="editorValue"
          :jsonValue="pendingEditJson"
          :focusEditor="editing"
          :focusEnd="true"
          :triggerFocus="triggerFocus"
          :targetConfig="targetConfig"
          :referenceFileTargetConfig="referenceFileTargetConfig"
          :customExtensions="customExtensions"
          :referencesEnabled="referencesEnabled"
          @handleInput="onEditComment"
        />
        <button
          type="button"
          class="comment__expand-button"
          @click="$emit('expandEditor')"
        >
          <icon
            data="@icon/expand-open.svg"
            width="var(--icon-16)"
            height="var(--icon-16)"
          />
        </button>
      </div>
    </div>
    <div
      v-if="editing"
      class="upload-area__edit-buttons comment-content__edit-buttons"
    >
      <button
        type="button"
        class="create-item__action-menu-item link is-medium-500 add-custom-outline links-divider__item"
        @click="$emit('onCancelEdit')"
      >
        Cancel
      </button>

      <button
        type="button"
        class="create-item__action-menu-item link is-medium-500 add-custom-outline"
        @click="$emit('onEnter')"
      >
        Save
      </button>
    </div>
    <div
      v-if="filesOnly.length"
      class="comment-content__files"
    >
      <div
        v-for="file in filesOnly"
        :key="file.id"
        class="comment-content__file-wrapper"
        :class="{ 'is-deleted': file.deleted }"
      >
        <FileItem
          :file="file"
          :files="comment.fileMetadata"
          :projectScoped="projectScoped"
          fancyboxGroup="comment_tab_fancybox"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { useStore } from "vuex"

import AvatarGroupTasks from "@/components/Avatar/AvatarGroupTasks.vue"
import EditorInput from "@/components/Editor/EditorInput.vue"
import FileItem from "@/components/File/FileItem.vue"
import { ReferencedText } from "@/components/Reference"
import StatusComponent from "@/components/StatusComponent/index.vue"

import { Comment, ItemStatus, reviewStatuses, Status, TypeConfig } from "@/models"
import { ReferenceAllUser } from "@/plugins/extensions/reference/referenceAt"
import { ReferenceHashFileLink } from "@/plugins/extensions/reference/referenceHash"
import { Getters } from "@/store"

import type { PropType } from "vue"

const props = defineProps({
  comment: {
    type: Object as PropType<Comment>,
    required: true
  },
  targetConfig: {
    type: Object as PropType<TypeConfig | null>,
    default: null
  },
  referenceFileTargetConfig: {
    type: Object as PropType<TypeConfig | null>,
    default: null
  },
  editing: {
    type: Boolean,
    default: false
  },
  pendingEditHtml: {
    type: String,
    default: ""
  },
  pendingEditJson: {
    type: String,
    default: ""
  },
  triggerFocus: {
    type: Boolean,
    default: false
  },
  projectScoped: {
    type: Boolean,
    default: true
  },
  referencesEnabled: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(["onEnter", "onEditComment", "onCancelEdit", "expandEditor"])

const store = useStore()

const beforeStatus = computed(() => props.comment.before?.status ?? "")
const afterStatus = computed(() => props.comment.after?.status ?? "")
const statusChangeReason = computed(() => props.comment.structuredDescription?.html ?? "")
const editorValue = computed(
  () => props.pendingEditHtml || props.comment.structuredDescription?.html || ""
)
const customExtensions = computed(() => {
  return props.referencesEnabled && props.referenceFileTargetConfig
    ? [ReferenceAllUser, ReferenceHashFileLink]
    : []
})
const showReviewAssignees = computed(() => {
  return (
    reviewStatuses.includes(afterStatus.value as ItemStatus) && props.comment.assignees.length > 0
  )
})
const filesOnly = computed(() => {
  return props.comment.fileMetadata?.filter(file => !file.isImage && !file.isVideo) ?? []
})

const getStatusById = (statusId: string): Status => {
  return store.getters[Getters.GET_STATUS_BY_ID](statusId)
}

function onEditComment(e: { getEmailText: () => string; html: string; json: unknown }) {
  emit("onEditComment", e)
}
</script>
